import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { getItems, getFirstItems, createFirstItem, updateFirstItem, getSecondItems, createSecondItem, updateSecondItem, getThirdItems, createThirdItem, updateThirdItem } from '../api/evaluationItem.api';
import { IItem, IGetItems, IFirstItem, IGetFirstItems, ISecondItem, IGetSecondItems, IThirdItem, IGetThirdItems } from '../models/evaluationItem.model';

interface ICriteriaState {
  items?: IItem[];
  firstItems?: IFirstItem[];
  secondItems?: ISecondItem[];
  thirdItems?: IThirdItem[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ICriteriaState = {
  items: [],
  firstItems: [],
  secondItems: [],
  thirdItems: [],
  isLoading: false
};

const criteriaSlicer = createSlice({
  name: 'criteria',
  initialState,
  reducers: {
    getItemsStart: (state: ICriteriaState) => {
      state.items = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getItemsSuccess: (state: ICriteriaState, action: PayloadAction<IGetItems>) => {
      state.items = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getItemsFailure: (state: ICriteriaState, action: PayloadAction<AxiosError | string>) => {
      state.items = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getFirstItemsStart: (state: ICriteriaState) => {
      state.firstItems = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getFirstItemsSuccess: (state: ICriteriaState, action: PayloadAction<IGetFirstItems>) => {
      state.firstItems = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getFirstItemsFailure: (state: ICriteriaState, action: PayloadAction<AxiosError|string>) => {
      state.firstItems = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSecondItemsStart: (state: ICriteriaState) => {
      state.secondItems = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSecondItemsSuccess: (state: ICriteriaState, action: PayloadAction<IGetSecondItems>) => {
      state.secondItems = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSecondItemsFailure: (state: ICriteriaState, action: PayloadAction<AxiosError | string>) => {
      state.secondItems = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getThirdItemsStart: (state: ICriteriaState) => {
      state.thirdItems = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getThirdItemsSuccess: (state: ICriteriaState, action: PayloadAction<IGetThirdItems>) => {
      state.thirdItems = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getThirdItemsFailure: (state: ICriteriaState, action: PayloadAction<AxiosError | string>) => {
      state.thirdItems = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ICriteriaState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (state: ICriteriaState, action: PayloadAction<AxiosError|string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ICriteriaState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ICriteriaState) => {
      state.isLoading = false;
      state.error = undefined;
    }
  }
});

export const {
  getItemsStart,
  getItemsSuccess,
  getItemsFailure,
  getFirstItemsStart,
  getFirstItemsSuccess,
  getFirstItemsFailure,
  getSecondItemsStart,
  getSecondItemsSuccess,
  getSecondItemsFailure,
  getThirdItemsStart,
  getThirdItemsSuccess,
  getThirdItemsFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess
} = criteriaSlicer.actions;

export default criteriaSlicer.reducer;

export const doGetItems = (): any => async (dispatch: any) => {
  try {
    dispatch(getItemsStart());
    const response = await getItems();
    dispatch(getItemsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getItemsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetFirstItems = (): any => async (dispatch: any) => {
  try {
    dispatch(getFirstItemsStart());
    const response = await getFirstItems();
    dispatch(getFirstItemsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getFirstItemsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateFirstItem = (data: IFirstItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createFirstItem(data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doUpdateFirstItem = (itemCode: number, data: IFirstItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await updateFirstItem(itemCode, data);
    dispatch(updateSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetSecondItems = (parentCode: number): any => async (dispatch: any) => {
  try {
    dispatch(getSecondItemsStart());
    const response = await getSecondItems(parentCode);
    dispatch(getSecondItemsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getSecondItemsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateSecondItem = (parentCode: number, data: ISecondItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createSecondItem(parentCode, data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doUpdateSecondItem = (parentCode: number, data: ISecondItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await updateSecondItem(parentCode, data);
    dispatch(updateSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetThirdItems = (parentCode: number): any => async (dispatch: any) => {
  try {
    dispatch(getThirdItemsStart());
    const response = await getThirdItems(parentCode);
    dispatch(getThirdItemsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getThirdItemsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateThirdItem = (parentCode: number, data: IThirdItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createThirdItem(parentCode, data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doUpdateThirdItem = (itemCode: number, data: IThirdItem): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await updateThirdItem(itemCode, data);
    dispatch(updateSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};
